import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { AssetRiskResult } from '../api/types';
import type { InsightDocument } from '../api/insights';
import { useAuth } from '../auth/AuthContext';
import { moduleLabel, orderModules, TECHNICAL_EXPOSURE } from '../lib/riskVocabulary';
import { LevelBadge } from '../components/cockpit/primitives';
import { PRIORITY_STYLE, PriorityChip, timeMargin, windowLabel } from '../components/cockpit/views';

/**
 * Read-only asset risk view (C-034): what the latest run says about one asset — Overall risk, the two
 * views, every dimension, priority, the out-of-time test and how many cryptographic artifacts are on
 * record. Writers get an Edit button; everyone else reads. All figures come from the latest completed
 * run (its insight document and its stored results), so they match the Overview.
 */

const LEAF = ['timeline', 'data_risk', 'supply_chain', 'migration_feasibility', 'threat', 'cryptography', 'compliance'];

interface Inventory { asset: { name: string }; total_artifacts: number; groups: Array<{ type: string; count: number }> }

/**
 * The out-of-time test in words. "Not testable" only when the engine could not apply the test
 * (status "unknown": no planning year or missing inputs); the figure is the one the reports print.
 */
export function timeStatusText(t: Parameters<typeof timeMargin>[0] | undefined): string {
  if (!t) return 'Not testable (inputs missing)';
  const m = timeMargin(t);
  const yrs = (v: string) => `${v} ${Number(v) === 1 ? 'yr' : 'yrs'}`;
  switch (t.status) {
    case 'late': { const w = windowLabel(t).text; return w === '—' ? 'Out of time' : `Out of time · ${w}`; }
    case 'tight': return m === null || Number(m) === 0 ? 'In time, no time to spare' : `In time, two years or less to spare (${yrs(m)} to spare)`;
    case 'on_track': return m === null ? 'In time' : `In time (${yrs(m)} to spare)`;
    case 'migrated': return 'Already quantum-safe';
    default: return 'Not testable (inputs missing)';
  }
}

export function AssetRiskView() {
  const { projectId, assetId } = useParams<{ projectId: string; assetId: string }>();
  const { hasRole } = useAuth();
  const canEdit = hasRole('admin', 'super_user', 'analyst');

  const doc = useQuery({
    queryKey: ['project-insights', projectId],
    queryFn: () => api.get<InsightDocument>(`/projects/${projectId}/insights/latest`),
    retry: false,
  });
  const runId = doc.data?.run_id;
  const results = useQuery({
    queryKey: ['run-results', runId, assetId],
    queryFn: () => api.get<AssetRiskResult[]>(`/runs/${runId}/results?asset_id=${assetId}`),
    enabled: !!runId,
  });
  const inventory = useQuery({
    queryKey: ['asset-inventory-count', projectId, assetId],
    queryFn: () => api.get<Inventory>(`/projects/${projectId}/assets/${assetId}/crypto-artifacts`),
  });

  const reg = doc.data?.risk_register.find((r) => r.asset_id === assetId);
  const time = doc.data?.time_window.rows.find((t) => t.asset_id === assetId);
  const name = reg?.asset_name ?? inventory.data?.asset.name ?? 'Asset';
  const byModule = new Map((results.data ?? []).map((r) => [r.module_code as string, r]));
  const dims = orderModules(LEAF);

  return (
    <div className="max-w-4xl mx-auto px-6 py-6" data-testid="asset-risk-view">
      <div className="mb-2">
        <Link to={`/projects/${projectId}?tab=assets`} className="text-xs text-ink-500 hover:text-ink-900">← Back to project</Link>
      </div>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl text-ink-900">{name}</h1>
          {reg && <p className="text-xs text-ink-500 mt-1">{[reg.asset_type, reg.business_unit, reg.system].filter(Boolean).join(' · ')}</p>}
        </div>
        {canEdit && (
          <Link to={`/assets/${assetId}/edit?project=${projectId}`} className="btn-secondary text-xs no-underline" data-testid="asset-edit">Edit asset</Link>
        )}
      </div>

      {doc.isLoading && <p className="text-sm text-ink-500">Loading…</p>}
      {!doc.isLoading && !reg && (
        <p className="text-sm text-ink-500" data-testid="asset-not-assessed">This asset has no Overall risk in the latest completed run. Run an assessment to score it.</p>
      )}

      {reg && (
        <>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="border border-ink-200 rounded-sm p-3">
              <dt className="text-[11px] uppercase tracking-wider text-ink-500">Priority</dt>
              <dd className="mt-1 text-sm text-ink-900" data-testid="asset-priority">{reg.priority ? <><PriorityChip p={reg.priority} /> {PRIORITY_STYLE[reg.priority].label}</> : '—'}</dd>
            </div>
            <div className="border border-ink-200 rounded-sm p-3">
              <dt className="text-[11px] uppercase tracking-wider text-ink-500">Overall risk</dt>
              <dd className="mt-1"><LevelBadge level={reg.overall?.level ?? null} score={reg.overall?.score ?? null} /></dd>
            </div>
            <div className="border border-ink-200 rounded-sm p-3">
              <dt className="text-[11px] uppercase tracking-wider text-ink-500">Baseline risk</dt>
              <dd className="mt-1"><LevelBadge level={reg.baseline?.level ?? null} score={reg.baseline?.score ?? null} /></dd>
            </div>
            <div className="border border-ink-200 rounded-sm p-3">
              <dt className="text-[11px] uppercase tracking-wider text-ink-500">{TECHNICAL_EXPOSURE.label}</dt>
              <dd className="mt-1"><LevelBadge level={reg.exposure_view?.level ?? null} score={reg.exposure_view?.score ?? null} /></dd>
            </div>
          </dl>

          <section className="mb-6">
            <h2 className="text-sm font-medium text-ink-900 mb-2">Out-of-time test</h2>
            <p className="text-sm text-ink-700" data-testid="asset-time">
              {timeStatusText(time ?? reg.time)}
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-sm font-medium text-ink-900 mb-2">Scores by dimension</h2>
            <table className="w-full text-sm border border-ink-100" data-testid="asset-dimensions">
              <tbody className="divide-y divide-ink-100">
                {dims.map((m) => {
                  const r = byModule.get(m);
                  const na = !r || r.risk_level === 'NA';
                  return (
                    <tr key={m}>
                      <td className="px-3 py-1.5 text-ink-700">{moduleLabel(m)}</td>
                      <td className="px-3 py-1.5 text-right">{na ? <span className="text-xs text-ink-400">{r ? 'Not applicable or data missing' : '—'}</span> : <LevelBadge level={r.risk_level} score={r.normalized_score == null ? null : Number(r.normalized_score)} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </>
      )}

      <section>
        <h2 className="text-sm font-medium text-ink-900 mb-2">Cryptographic inventory</h2>
        <p className="text-sm text-ink-700" data-testid="asset-artifacts">
          {inventory.data
            ? inventory.data.total_artifacts === 0
              ? 'No cryptographic artifacts recorded.'
              : `${inventory.data.total_artifacts} ${inventory.data.total_artifacts === 1 ? 'artifact' : 'artifacts'} recorded (${inventory.data.groups.filter((g) => g.count > 0).map((g) => `${g.count} ${g.type.replace('_', ' ')}`).join(', ')}).`
            : inventory.isLoading ? 'Loading…' : '—'}
        </p>
      </section>
    </div>
  );
}
